import { useState, type ReactNode } from "react";
import { DateTime } from "luxon";
import { CalendarIcon, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { PeriodKey } from "@/types/filters";
import { PimsLaunchDatePicker } from "@/components/dashboard/PimsLaunchDatePicker";
import type { LaunchDateOption } from "@/lib/launchCohort";

export interface PeriodState {
  period: PeriodKey;
  asOfDate: string;
  customStart: string;
  customEnd: string;
}

// The two families are rendered as separate groups because they answer
// different questions: trailing windows for fair comparison, calendar windows
// for reporting to a monthly or annual number.
const TRAILING_PERIODS: PeriodKey[] = ["T1W", "T4W", "T13W", "T52W"];
const CALENDAR_PERIODS: PeriodKey[] = ["PM", "MTD", "YTD"];

const DEFAULT_PERIOD: PeriodKey = "T1W";

// Both rows used to spell their rules out in a paragraph underneath. Together
// those ran to six lines above the fold, for text that is read once and then
// only when something surprises you -- so they moved behind the label instead.
function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          className="shrink-0 rounded text-gray-400 transition-colors hover:text-gray-600"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        showArrow={false}
        className="max-w-[420px] border border-black/10 bg-white text-[11px] leading-[1.5] text-gray-600 shadow-lg"
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

interface PeriodBarProps {
  periodState: PeriodState;
  setPeriod: (p: PeriodKey) => void;
  setAsOfDate: (date: string) => void;
  // Sits with the period rather than with scope because it is a property of the
  // COMPARISON, not of the selection: which sites are old enough for this
  // period's prior year to exist.
  launchedBefore: string | null;
  setLaunchedBefore: (date: string | null) => void;
  launchDateOptions: LaunchDateOption[];
  firstLaunchDate: string | null;
  // The selected site's own go-live, or null unless the scope is narrowed to
  // one site. Reported beside the cutoff because that is where the question
  // "why does this site have no prior year?" gets asked.
  siteLaunchDate: string | null;
}

export function PeriodBar({
  periodState,
  setPeriod,
  setAsOfDate,
  launchedBefore,
  setLaunchedBefore,
  launchDateOptions,
  firstLaunchDate,
  siteLaunchDate,
}: PeriodBarProps) {
  const { period, asOfDate } = periodState;
  const [calendarOpen, setCalendarOpen] = useState(false);

  const calendarDate = asOfDate ? DateTime.fromISO(asOfDate).toJSDate() : undefined;

  const today = DateTime.now().toISODate()!;
  const isDefault = period === DEFAULT_PERIOD && asOfDate === today;

  function resetPeriod() {
    setPeriod(DEFAULT_PERIOD);
    setAsOfDate(today);
  }

  return (
    <TooltipProvider>
      <div className="bg-[color-mix(in_srgb,var(--brand-cream)_60%,white)] rounded-xl px-4 py-2.5 mb-3 flex flex-row gap-4 border border-black/5">
        <div className="flex flex-col gap-2 flex-1">
          {/* Period row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500">
              Scheduled Period
              <InfoHint label="Scheduled Period">
                T1W / T4W / T13W / T52W = the last 1 / 4 / 13 / 52 whole Monday–Sunday weeks,
                matched to the same week numbers a year earlier · PM / MTD / YTD = previous month /
                month to date / year to date, counted in calendar days and matched to the same dates
                a year earlier · every period counts back from the As of date
              </InfoHint>
            </span>

            {[TRAILING_PERIODS, CALENDAR_PERIODS].map((group, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <span className="w-px h-5 bg-black/15" />}
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={period}
                  onValueChange={(v) => {
                    if (v) setPeriod(v as PeriodKey);
                  }}
                >
                  {group.map((p) => (
                    <ToggleGroupItem
                      key={p}
                      value={p}
                      className="h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10"
                    >
                      {p}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            ))}

            <span className="text-[11px] text-gray-400 ml-1">As of</span>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[12px] font-normal gap-1.5 border-black/15 bg-white"
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {asOfDate ? DateTime.fromISO(asOfDate).toFormat("MMM d, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={calendarDate}
                  onSelect={(d) => {
                    if (d) {
                      setAsOfDate(DateTime.fromJSDate(d).toISODate()!);
                      setCalendarOpen(false);
                    }
                  }}
                />
                <div className="border-t border-black/5 p-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-7 text-[12px] font-normal border-black/15"
                    onClick={() => {
                      setAsOfDate(today);
                      setCalendarOpen(false);
                    }}
                  >
                    Today
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {!isDefault && (
              <button
                onClick={resetPeriod}
                className="text-[11px] px-2.5 py-1 rounded-md border border-black/15 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            )}
          </div>

          {/* PIMS cohort row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500">
              PIMS Launched Before
              <InfoHint label="PIMS Launched Before">
                PIMS Launched Before keeps only sites live on the new PIMS by that date, so both
                sides of a vs. PY comparison cover the same sites
              </InfoHint>
            </span>
            <PimsLaunchDatePicker
              value={launchedBefore}
              onChange={setLaunchedBefore}
              options={launchDateOptions}
              firstLaunchDate={firstLaunchDate}
            />
            {launchedBefore && (
              <button
                onClick={() => setLaunchedBefore(null)}
                className="text-[11px] px-2.5 py-1 rounded-md border border-black/15 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
            )}

            {/* Reads out rather than filters, so it is a pill and not a
                control -- and it says "selected site" because this row already
                holds a date that means something else entirely. The cutoff is a
                cohort boundary; this is one site's own go-live. */}
            {siteLaunchDate && (
              <span
                title={`The selected site went live on the new PIMS on ${DateTime.fromISO(
                  siteLaunchDate
                ).toFormat(
                  "cccc, d LLLL yyyy"
                )}. Scheduling before that date came out of the system it replaced and is not loaded, which is why a site that went live recently has no prior year to compare against.`}
                className="ml-1 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 bg-white px-2.5 py-1 text-[11px] text-gray-500"
              >
                Selected site live
                <span className="font-semibold tabular-nums text-gray-700">
                  {DateTime.fromISO(siteLaunchDate).toFormat("MMM d, yyyy")}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
